import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { UpdatePasswordRequest } from "src/core/backend/clients/backendClient";
import { ToastContext } from "src/core/context/ToastContext";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useError } from "src/core/hooks/useError";
import { logService } from "src/core/services/logService";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import {
  Password2Field,
  type Password2FormData,
} from "src/features/shared/components/Password2Field";
import {
  PasswordField,
  type PasswordFormData,
} from "src/features/shared/components/PasswordField";
import "./ResetPasswordPage.css";

export interface ResetPasswordFormData
  extends PasswordFormData,
    Password2FormData {}

export function ResetPasswordPage() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useError();

  const form = useForm<ResetPasswordFormData>({
    mode: "all",
  });

  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();
  const errorHandler = useErrorHandler();

  const [key, setKey] = useState<string>();

  useEffect(() => {
    if ((settingsStore?.errorList?.errorCodes?.length ?? 0) === 0) return;

    const queryParams = new URLSearchParams(location.search);

    logService.log("resetpassword", queryParams);

    const key = queryParams.get("key");
    if (!key) {
      errorHandler.setError({
        title: t("Home.ResetPassword.Title"),
        message: t("Home.ResetPassword.Error"),
      });

      navigate("/");
    } else {
      setKey(key);
    }
  }, [settingsStore.errorList]);

  const onSave = async () => {
    if (!form.formState.isValid) return;

    const formValues = form.getValues();

    logService.log("ResetPasswordPage.onSave", formValues);

    const request: UpdatePasswordRequest = {
      password: formValues.password,
      key: key ?? "",
    };

    await authStore
      .resetPassword(request)
      .then(() => {
        toastRef?.current?.show({
          severity: "success",
          summary: t("Home.ResetPassword.Title"),
          detail: t("Home.ResetPassword.Success"),
          closable: true,
          life: 15000,
        });
        navigate("/");
      })
      .catch((error) => {
        errorHandler.setError({
          title: t("Home.ResetPassword.Title"),
          message: t("Home.ResetPassword.Error") + " " + getErrorMessage(error),
        });
      });
  };

  return (
    <form>
      <div className="resetPasswordPage flex justify-content-center">
        <Card
          title={t("Home.ResetPassword.Title")}
          className="card"
          footer={
            <div className="flex justify-content-end gap-2">
              <Button
                type="button"
                label={t("Home.Profile.Save")}
                icon="pi pi-check"
                severity="success"
                disabled={!form.formState.isValid}
                loading={authStore.loading}
                onClick={onSave}
              ></Button>

              <Button
                type="button"
                label={t("Base.Cancel")}
                icon="pi pi-times"
                severity="warning"
                onClick={() => navigate("/")}
              ></Button>
            </div>
          }
        >
          <div className="field grid">
            <label
              htmlFor="password"
              className="col-12 md:col-4 font-bold"
            >
              {t("Home.Login.Password")} *:
            </label>
            <div className="col-12 md:col-8">
              <PasswordField
                form={form}
                feedback={true}
                required={true}
              />
            </div>
          </div>

          <div className="field grid">
            <label
              htmlFor="password2"
              className="col-12 md:col-4 font-bold white-space-nowrap"
            >
              {t("Home.Registration.Password2")} *:
            </label>
            <div className="col-12 md:col-8">
              <Password2Field
                form={form}
                required={true}
              />
            </div>
          </div>
        </Card>
      </div>
    </form>
  );
}
